import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Loader, Icon } from '@saviynt/design-system';
import { makeStyles, Typography } from '@material-ui/core';

import useObjectSelection from '../../hooks/useObjectSelection';
import useObjectTypeCounts from '../../hooks/useObjectTypeCounts';
import useFetchObjects from '../../hooks/useFetchObjects';
import { setMigratedConfigObjects } from '../../redux/migrationSlice';
import useMigrationProgress from '../../hooks/useMigrationProgress';
import {
  STATUS,
  SKIP_SAV_CONFIG_TYPES,
  BULK_MIGRATE_DATASET_TYPES,
} from '../../constants/migrationConstants';
import {
  PROGRESS_MESSAGES,
  STATUS_LABELS,
  OBJECT_TYPE_LABELS,
  ERROR_MESSAGES,
  UI_LABELS,
} from '../../constants/stringConstants';
import {
  generateSavConfig,
  bulkOnboardObjects,
  bulkOnboardObjectsV2,
  bulkMigrateDataset,
  fetchMigrationDisabledObjects,
  fetchSourceRawData,
} from '../../redux/migrationThunks';

import ObjectTypeGrid from './ObjectTypeGrid';
import ObjectListView from './ObjectListView';
import SelectionBar from './SelectionBar';
import PreviewModal from '../shared/PreviewModal';
import SaviyntConfigForm from '../shared/SaviyntConfigForm';
import PasswordPolicyForm from '../shared/PasswordPolicyForm';
import WorkGroupForm from '../shared/WorkGroupForm';

const useStyles = makeStyles({
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    gap: '12px',
  },
  errorIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#dc3545',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorTitle: {
    fontWeight: 600,
    fontSize: '16px',
    color: '#1a1a1a',
  },
  errorMessage: {
    fontSize: '14px',
    color: '#6b7280',
  },
});

function ConfigurationMigration({ sourceType, sourceID, onMigrationUpdate }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { sourceStatus } = useSelector((state) => state.migration);
  const userAttributeMappingConfigured = !!sourceStatus?.userAttributeMappingConfigured;
  const [selectedObjectType, setSelectedObjectType] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [sourceId, setSourceId] = useState(sourceID);
  const [activeTab, setActiveTab] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ title: '', data: '', type: 'source' });
  const [modalLoading, setModalLoading] = useState(false);
  const isFetchingPreviewRef = useRef(false);
  const [saviyntFormOpen, setSaviyntFormOpen] = useState(false);
  const [saviyntFormData, setSaviyntFormData] = useState(null);
  const [passwordPolicyFormOpen, setPasswordPolicyFormOpen] = useState(false);
  const [passwordPolicyFormData, setPasswordPolicyFormData] = useState(null);
  const [workGroupFormOpen, setWorkGroupFormOpen] = useState(false);
  const [workGroupFormData, setWorkGroupFormData] = useState(null);
  const [currentObject, setCurrentObject] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [migrationDisabled, setMigrationDisabled] = useState({});
  const prevMigratingRef = useRef(false);

  useEffect(() => {
    const wasMigrating = prevMigratingRef.current;
    prevMigratingRef.current = migrating;

    // Only fetch on initial load or when migration just finished (true → false)
    const migrationJustFinished = wasMigrating && !migrating;
    if (!migrationJustFinished && wasMigrating) return;

    if (sourceId && selectedObjectType === OBJECT_TYPE_LABELS.APPLICATION) {
      dispatch(
        fetchMigrationDisabledObjects({ sourceId, objectType: OBJECT_TYPE_LABELS.APPLICATION })
      )
        .unwrap()
        .then((res) => setMigrationDisabled(res?.data?.migrationBlocked ?? {}))
        .catch(() => setMigrationDisabled({}));
    } else {
      setMigrationDisabled({});
    }
  }, [sourceId, selectedObjectType, dispatch, migrating]);

  const {
    progress: migrationProgress,
    startProgress,
    updateProgress,
    clearProgress,
  } = useMigrationProgress();

  useEffect(() => {
    setSourceId(sourceID);
  }, [sourceID]);

  const {
    objectTypeCounts,
    automatedMigration,
    assistedMigration,
    loading: countsLoading,
    error: countsError,
  } = useObjectTypeCounts(sourceId, sourceType);
  const { objects, loading, setObjects } = useFetchObjects(
    sourceId,
    selectedObjectType,
    sourceType
  );

  const filteredObjects = objects
    .filter((obj) => {
      const matchesSearch = obj.objectName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'ready' && (!obj.status || obj.status === STATUS.STATUS_PENDING)) ||
        (statusFilter === 'completed' && obj.status === STATUS.STATUS_COMPLETED) ||
        (statusFilter === 'failed' && obj.status === STATUS.STATUS_FAILED);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const aCompleted = a.status === STATUS.STATUS_COMPLETED ? 1 : 0;
      const bCompleted = b.status === STATUS.STATUS_COMPLETED ? 1 : 0;
      if (aCompleted !== bCompleted) return aCompleted - bCompleted;

      let compareValue = 0;
      if (sortBy === 'name') {
        compareValue = a.objectName.localeCompare(b.objectName);
      } else if (sortBy === 'created' || sortBy === 'modified') {
        const parseMeta = (s) => {
          try {
            return s ? JSON.parse(s) : {};
          } catch {
            return {};
          }
        };
        const aTime = parseMeta(a.metadata)[sortBy] ?? 0;
        const bTime = parseMeta(b.metadata)[sortBy] ?? 0;
        compareValue = aTime - bTime;
      }
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
  const {
    selectedObjects,
    selectedCount,
    handleSelectAll,
    handleClearAll,
    handleToggleObject,
    isSelected,
  } = useObjectSelection(filteredObjects.filter((obj) => obj.status !== STATUS.STATUS_COMPLETED));

  const handleMigrate = useCallback(async () => {
    if (selectedCount === 0) return;
    setMigrating(true);
    const objectsToMigrate = objects.filter((obj) => selectedObjects.includes(obj.id));

    // Object types that don't need /sav-config call
    const normalizedType = selectedObjectType.replace(/\s/g, '');
    const useBulkMigrateDataset = BULK_MIGRATE_DATASET_TYPES.includes(normalizedType);
    const skipSavConfig = SKIP_SAV_CONFIG_TYPES.includes(normalizedType);
    const isSailpointApplication =
      !sourceType?.includes('oim') && normalizedType === OBJECT_TYPE_LABELS.APPLICATION;
    const isSailpointBundle =
      !sourceType?.includes('oim') && normalizedType === OBJECT_TYPE_LABELS.BUNDLE;

    try {
      objectsToMigrate.forEach((obj) => {
        startProgress(obj.id, PROGRESS_MESSAGES.STARTING_MIGRATION);
      });

      let validApplications;

      if (isSailpointApplication || isSailpointBundle) {
        // SailPoint Application — skip sav-config, go straight to v2 bulk-onboard
        const objectIds = objectsToMigrate.map((obj) => obj.id);
        objectsToMigrate.forEach((obj) => {
          updateProgress(obj.id, STATUS_LABELS.IN_PROGRESS, PROGRESS_MESSAGES.ONBOARDING_SAVIYNT);
        });
        setObjects((prev) =>
          prev.map((obj) =>
            objectIds.includes(obj.id) ? { ...obj, status: STATUS.STATUS_IN_PROGRESS } : obj
          )
        );
        try {
          const bulkResult = await dispatch(
            bulkOnboardObjectsV2({
              sourceId,
              objectType: selectedObjectType,
              objectIds,
            })
          ).unwrap();
          if (bulkResult?.data?.results) {
            bulkResult.data.results.forEach((result) => {
              const isSuccess = result.status === STATUS_LABELS.SUCCESS;
              const errorMessage =
                result.errorDetails ||
                result.message ||
                (isSuccess
                  ? PROGRESS_MESSAGES.MIGRATION_COMPLETED
                  : PROGRESS_MESSAGES.MIGRATION_FAILED);
              setObjects((prev) =>
                prev.map((obj) =>
                  obj.id === result.objectId
                    ? { ...obj, status: isSuccess ? STATUS.STATUS_COMPLETED : STATUS.STATUS_FAILED }
                    : obj
                )
              );
              updateProgress(
                result.objectId,
                isSuccess ? STATUS_LABELS.COMPLETED : STATUS_LABELS.FAILED,
                errorMessage
              );
              setTimeout(() => clearProgress(result.objectId), 5000);
            });
            const successCount = bulkResult.data.results.filter(
              (r) => r.status === STATUS_LABELS.SUCCESS
            ).length;
            if (onMigrationUpdate) onMigrationUpdate(successCount, objectsToMigrate.length);
            dispatch(setMigratedConfigObjects(successCount));
          }
        } catch (bulkError) {
          const errorResults = bulkError?.data?.data?.results;
          if (errorResults) {
            errorResults.forEach((result) => {
              const isSuccess = result.status === STATUS_LABELS.SUCCESS;
              const errorMessage =
                result.errorDetails || result.message || PROGRESS_MESSAGES.MIGRATION_FAILED;
              setObjects((prev) =>
                prev.map((obj) =>
                  obj.id === result.objectId
                    ? { ...obj, status: isSuccess ? STATUS.STATUS_COMPLETED : STATUS.STATUS_FAILED }
                    : obj
                )
              );
              updateProgress(
                result.objectId,
                isSuccess ? STATUS_LABELS.COMPLETED : STATUS_LABELS.FAILED,
                errorMessage
              );
              setTimeout(() => clearProgress(result.objectId), 5000);
            });
          } else {
            objectsToMigrate.forEach((obj) => {
              const errorMessage = bulkError?.message || PROGRESS_MESSAGES.BULK_ONBOARD_FAILED;
              setObjects((prev) =>
                prev.map((o) => (o.id === obj.id ? { ...o, status: STATUS.STATUS_FAILED } : o))
              );
              updateProgress(obj.id, STATUS_LABELS.FAILED, errorMessage);
              setTimeout(() => clearProgress(obj.id), 5000);
            });
          }
        }
        handleClearAll();
        return;
      }

      if (skipSavConfig) {
        // For Datasets, UserGroups, Custom - skip config generation
        validApplications = objectsToMigrate.map((object) => ({
          objectId: object.id,
          configuration: {}, // Empty config for these types
        }));
        objectsToMigrate.forEach((obj) => {
          updateProgress(obj.id, STATUS_LABELS.IN_PROGRESS, PROGRESS_MESSAGES.PREPARING_MIGRATION);
        });
      } else {
        // For other types - generate config
        const applications = await Promise.all(
          objectsToMigrate.map(async (object) => {
            try {
              updateProgress(
                object.id,
                STATUS_LABELS.IN_PROGRESS,
                `Generating ${selectedObjectType} ${PROGRESS_MESSAGES.GENERATING_CONFIG}`
              );

              const config = await dispatch(
                generateSavConfig({
                  sourceId,
                  objectIds: [object.id],
                  objectType: selectedObjectType,
                })
              ).unwrap();

              // Validate configuration based on object type
              let hasValidConfig;
              if (normalizedType === OBJECT_TYPE_LABELS.WORKGROUP) {
                hasValidConfig = config?.data?.configuration?.usergroup;
              } else if (normalizedType === OBJECT_TYPE_LABELS.PASSWORD_POLICY) {
                hasValidConfig = config?.data?.configuration?.policyname;
              } else if (normalizedType === OBJECT_TYPE_LABELS.APPLICATION) {
                hasValidConfig = config?.data?.configuration?.applicationType;
              } else {
                hasValidConfig = config?.data?.configuration != null;
              }

              if (!hasValidConfig) {
                updateProgress(
                  object.id,
                  STATUS_LABELS.FAILED,
                  PROGRESS_MESSAGES.CONFIG_GENERATION_FAILED
                );
                setObjects((prev) =>
                  prev.map((obj) =>
                    obj.id === object.id ? { ...obj, status: STATUS.STATUS_FAILED } : obj
                  )
                );
                return null;
              }

              if (
                normalizedType === OBJECT_TYPE_LABELS.APPLICATION &&
                !sourceType?.includes('oim') &&
                (!config?.data?.configuration?.connection ||
                  !config?.data?.configuration?.securitySystem ||
                  !config?.data?.configuration?.endpoint)
              ) {
                updateProgress(
                  object.id,
                  STATUS_LABELS.FAILED,
                  PROGRESS_MESSAGES.MISSING_REQUIRED_CONFIG
                );
                setObjects((prev) =>
                  prev.map((obj) =>
                    obj.id === object.id ? { ...obj, status: STATUS.STATUS_FAILED } : obj
                  )
                );
                return null;
              }

              updateProgress(
                object.id,
                STATUS_LABELS.IN_PROGRESS,
                PROGRESS_MESSAGES.ONBOARDING_SAVIYNT
              );

              return {
                objectId: object.id,
                configuration: config?.data?.configuration,
              };
            } catch (error) {
              const errorMessage =
                error?.message || error?.errors?.[0] || PROGRESS_MESSAGES.CONFIG_GENERATION_FAILED;
              updateProgress(object.id, STATUS_LABELS.FAILED, errorMessage);
              setObjects((prev) =>
                prev.map((obj) =>
                  obj.id === object.id
                    ? { ...obj, status: STATUS.STATUS_FAILED, errorMessage }
                    : obj
                )
              );
              return null;
            }
          })
        );

        validApplications = applications.filter(Boolean);
      }

      if (validApplications.length > 0) {
        setObjects((prev) =>
          prev.map((obj) =>
            validApplications.some((app) => app.objectId === obj.id)
              ? { ...obj, status: STATUS.STATUS_IN_PROGRESS }
              : obj
          )
        );

        try {
          const objectIds = validApplications.map((app) => app.objectId);

          const bulkResult = useBulkMigrateDataset
            ? await dispatch(
                bulkMigrateDataset({
                  sourceId,
                  objectIds,
                  objectType: selectedObjectType,
                })
              ).unwrap()
            : await dispatch(
                bulkOnboardObjects({
                  sourceId,
                  objectType: selectedObjectType,
                  ...(skipSavConfig ? { objectIds } : { objects: validApplications }),
                })
              ).unwrap();

          if (bulkResult?.data?.results) {
            bulkResult.data.results.forEach((result) => {
              const isSuccess = useBulkMigrateDataset
                ? result.success
                : result.status === STATUS_LABELS.SUCCESS;
              const errorMessage =
                result.errorDetails ||
                result.message ||
                (isSuccess
                  ? PROGRESS_MESSAGES.MIGRATION_COMPLETED
                  : PROGRESS_MESSAGES.MIGRATION_FAILED);
              setObjects((prev) =>
                prev.map((obj) =>
                  obj.id === result.objectId
                    ? { ...obj, status: isSuccess ? STATUS.STATUS_COMPLETED : STATUS.STATUS_FAILED }
                    : obj
                )
              );
              updateProgress(
                result.objectId,
                isSuccess ? STATUS_LABELS.COMPLETED : STATUS_LABELS.FAILED,
                errorMessage
              );
              setTimeout(() => clearProgress(result.objectId), 5000);
            });
            const successCount = useBulkMigrateDataset
              ? bulkResult.data.results.filter((r) => r.success).length
              : bulkResult.data.results.filter((r) => r.status === STATUS_LABELS.SUCCESS).length;
            if (onMigrationUpdate) onMigrationUpdate(successCount, objectsToMigrate.length);
            dispatch(setMigratedConfigObjects(successCount));
          }
        } catch (bulkError) {
          const errorResults = bulkError?.data?.data?.results;

          if (errorResults) {
            errorResults.forEach((result) => {
              const isSuccess = result.status === STATUS_LABELS.SUCCESS;
              const errorMessage =
                result.errorDetails || result.message || PROGRESS_MESSAGES.MIGRATION_FAILED;
              setObjects((prev) =>
                prev.map((obj) =>
                  obj.id === result.objectId
                    ? { ...obj, status: isSuccess ? STATUS.STATUS_COMPLETED : STATUS.STATUS_FAILED }
                    : obj
                )
              );
              updateProgress(
                result.objectId,
                isSuccess ? STATUS_LABELS.COMPLETED : STATUS_LABELS.FAILED,
                errorMessage
              );
              setTimeout(() => clearProgress(result.objectId), 5000);
            });
          } else {
            // No results in error, mark all as failed
            validApplications.forEach((app) => {
              const errorMessage = bulkError?.message || PROGRESS_MESSAGES.BULK_ONBOARD_FAILED;
              setObjects((prev) =>
                prev.map((obj) =>
                  obj.id === app.objectId ? { ...obj, status: STATUS.STATUS_FAILED } : obj
                )
              );
              updateProgress(app.objectId, STATUS_LABELS.FAILED, errorMessage);
              setTimeout(() => clearProgress(app.objectId), 5000);
            });
          }
        }
      }
      handleClearAll();
    } catch (error) {
      setObjects((prev) =>
        prev.map((obj) =>
          objectsToMigrate.some((o) => o.id === obj.id)
            ? { ...obj, status: STATUS.STATUS_FAILED }
            : obj
        )
      );
    } finally {
      setMigrating(false);
    }
  }, [
    selectedCount,
    objects,
    selectedObjects,
    selectedObjectType,
    sourceType,
    sourceId,
    setObjects,
    handleClearAll,
    onMigrationUpdate,
    dispatch,
    startProgress,
    updateProgress,
    clearProgress,
  ]);

  const handleObjectTypeClick = useCallback(
    (objectType) => {
      setSelectedObjectType(objectType);
      handleClearAll();
    },
    [handleClearAll]
  );

  const handlePreview = useCallback(
    async (object, type) => {
      if (isFetchingPreviewRef.current) return;
      isFetchingPreviewRef.current = true;
      const sourceLabel =
        type === 'source'
          ? sourceType?.includes('oim')
            ? UI_LABELS.OIM
            : UI_LABELS.SAILPOINT
          : UI_LABELS.SAVIYNT;
      setModalData({ title: `${sourceLabel} - ${object.objectName}`, data: '', type });
      setModalOpen(true);
      setModalLoading(true);
      try {
        const res = await dispatch(fetchSourceRawData({ sourceId, objectId: object.id })).unwrap();
        setModalData({
          title: `${sourceLabel} - ${object.objectName}`,
          data: res || ERROR_MESSAGES.NO_DATA_AVAILABLE,
          type,
        });
      } catch {
        setModalData({
          title: `${sourceLabel} - ${object.objectName}`,
          data: ERROR_MESSAGES.NO_DATA_AVAILABLE,
          type,
        });
      } finally {
        setModalLoading(false);
        isFetchingPreviewRef.current = false;
      }
    },
    [sourceType, sourceId, dispatch]
  );

  const currentObjectType =
    automatedMigration?.find((t) => t.type === selectedObjectType) ||
    assistedMigration?.find((t) => t.type === selectedObjectType);

  if (countsLoading && !selectedObjectType) {
    return (
      <div className={classes.loader}>
        <Loader kind="loop" size="large" format="inline" color="brand" />
      </div>
    );
  }

  if (
    countsError ||
    (!automatedMigration.length && !assistedMigration.length && !sourceType?.includes('oim'))
  ) {
    return (
      <div className={classes.errorContainer}>
        <div className={classes.errorIcon}>
          <Icon
            kind="AlertCriticalOutline"
            color="--color-foreground-neutral-inverse"
            label="Error"
            size="large"
          />
        </div>
        <Typography variant="h6" className={classes.errorTitle}>
          {ERROR_MESSAGES.BACKEND_UNAVAILABLE}
        </Typography>
        <Typography variant="body2" className={classes.errorMessage}>
          {ERROR_MESSAGES.CANNOT_CONNECT_BACKEND}
        </Typography>
      </div>
    );
  }

  if (!selectedObjectType) {
    return (
      <ObjectTypeGrid
        automatedMigration={automatedMigration}
        assistedMigration={assistedMigration}
        objectTypeCounts={objectTypeCounts}
        onObjectTypeClick={handleObjectTypeClick}
        sourceType={sourceType}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    );
  }

  if (loading) {
    return (
      <div className={classes.loader}>
        <Loader kind="loop" size="large" format="inline" color="brand" />
      </div>
    );
  }

  return (
    <>
      <ObjectListView
        objects={filteredObjects}
        sourceType={sourceType}
        selectedCount={selectedCount}
        isSelected={isSelected}
        onSelectAll={(checked) => (checked ? handleSelectAll() : handleClearAll())}
        onObjectSelect={handleToggleObject}
        onBack={() => setSelectedObjectType(null)}
        onPreview={handlePreview}
        objectTypeLabel={currentObjectType?.label || selectedObjectType}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        filteredCount={filteredObjects.length}
        totalCount={objects.length}
        migrationProgress={migrationProgress}
        automatedMigration={automatedMigration}
        migrationDisabled={migrationDisabled}
        userAttributeMappingConfigured={userAttributeMappingConfigured}
      />

      <SelectionBar
        selectedCount={selectedCount}
        onClearAll={handleClearAll}
        onMigrate={handleMigrate}
        migrating={migrating}
      />

      <PreviewModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalData.title}
        data={modalData.data}
        type={modalData.type}
        isLoading={modalLoading}
      />

      <SaviyntConfigForm
        isOpen={saviyntFormOpen}
        onClose={() => {
          setSaviyntFormOpen(false);
          setSaviyntFormData(null);
          setCurrentObject(null);
        }}
        title={currentObject?.objectName || ''}
        data={saviyntFormData}
        onConfirm={() => {}}
      />

      <PasswordPolicyForm
        isOpen={passwordPolicyFormOpen}
        onClose={() => {
          setPasswordPolicyFormOpen(false);
          setPasswordPolicyFormData(null);
          setCurrentObject(null);
        }}
        title={currentObject?.objectName || ''}
        data={passwordPolicyFormData}
        onConfirm={() => {}}
      />

      <WorkGroupForm
        isOpen={workGroupFormOpen}
        onClose={() => {
          setWorkGroupFormOpen(false);
          setWorkGroupFormData(null);
          setCurrentObject(null);
        }}
        title={currentObject?.objectName || ''}
        data={workGroupFormData}
        onConfirm={() => {}}
      />
    </>
  );
}

ConfigurationMigration.propTypes = {
  sourceType: PropTypes.string.isRequired,
  sourceID: PropTypes.string,
  onMigrationUpdate: PropTypes.func,
};

ConfigurationMigration.defaultProps = {
  sourceID: null,
  onMigrationUpdate: null,
};

export default ConfigurationMigration;
